/**
 * 90-second auto-playing product tour at /tour. Designed to be
 * screen-recorded as MP4. Each slide auto-advances on a 14s rhythm
 * (configurable). Visitors can pause/resume with the spacebar.
 *
 * The tour is intentionally self-contained — no Lottie or video assets.
 * Pure CSS keyframes + Tailwind so it ships in the same bundle.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DeckDownloadModal from "@/components/marketing/DeckDownloadModal";
import {
  Play, Pause, ArrowRight, CaretLeft, CaretRight,
  ChatCircleDots, Database, Lightning, MapTrifold, Brain, ShieldCheck,
} from "@phosphor-icons/react";

const NAVY = "#0B1733";
const GOLD = "#C9A84C";

const SLIDES = [
  {
    eyebrow: "00 · The pitch",
    title: "Stop clicking. Start speaking. That's VĀKU.",
    body: "The AI super-agent for finance teams. One platform that reads, writes and executes across Controllership, FP&A, AP, AR, Tax and Treasury — directly inside Oracle, Dynamics and IFS.",
    icon: Lightning,
    mockup: "chat",
  },
  {
    eyebrow: "01 · Conversation",
    title: "Talk to your ERP in plain English",
    body: "Vaku listens for intent — create invoice, post journal, age suppliers, fix asset — and looks up the live Oracle catalog before you even hit send.",
    icon: ChatCircleDots,
    mockup: "chat",
  },
  {
    eyebrow: "02 · 4-Layer Wizard",
    title: "An interactive wizard that fills the gaps",
    body: "Lookups → conversational gap-fill → inline LLM autosuggest → dry-run validation. The fields you'd normally type are pre-filled from Oracle metadata.",
    icon: Lightning,
    mockup: "wizard",
  },
  {
    eyebrow: "03 · Telecom Solution",
    title: "Live GPS asset tracking + BSS bridge",
    body: "Point and linear assets on a live map, 5G slicing KPIs, revenue assurance, spectrum licensing — all tenant-scoped, all real-time.",
    icon: MapTrifold,
    mockup: "map",
  },
  {
    eyebrow: "04 · Vakuai-Native Database",
    title: "Every agent run becomes a row you own",
    body: "60+ Vakuai-native collections — one per agent, one per dashboard. Tenant-tagged from day one. Auditors love it.",
    icon: Database,
    mockup: "db",
  },
  {
    eyebrow: "05 · Admin Console",
    title: "Multi-tenant, multi-role, BYOK LLM",
    body: "Per-user menu access, per-agent overrides, bring-your-own LLM keys, and a Vakuai Database explorer that surfaces every tenant's data in one place.",
    icon: ShieldCheck,
    mockup: "admin",
  },
  {
    eyebrow: "06 · The CFO's morning",
    title: "From Slack to Oracle in 30 seconds",
    body: "An AI narrative for the inbox, anomalies flagged, variances explained, and the month-end orchestrator that closes books on schedule.",
    icon: Brain,
    mockup: "narrative",
    cta: true,
  },
];

const SLIDE_MS = 14_000;

// ----- Mockup components (lightweight, CSS-only) ------------------------------
const ChatMockup = () => (
  <div className="bg-white rounded-lg shadow-2xl p-4 w-[420px]">
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-3">vaku · chat</div>
    {[
      ["user", "Create a USD 12,500 invoice for ACME Corp dated April 1."],
      ["ai", "Looking up suppliers… found ACME Corp (Supplier #3081)."],
      ["ai", "Drafted: PO-required=No, BU=Vision Operations, GL Date=01-Apr-26. Ready to post?"],
    ].map(([who, msg], i) => (
      <div key={i} className={`mb-2 text-[12px] ${who === "user" ? "text-right" : ""}`}
        style={{ animation: `fadeUp 0.6s ease ${i * 0.4}s both` }}>
        <span className={`inline-block px-3 py-2 rounded-lg ${who === "user" ? "bg-[#0020C2] text-white" : "bg-gray-100 text-gray-900"}`}>
          {msg}
        </span>
      </div>
    ))}
  </div>
);

const WizardMockup = () => (
  <div className="bg-white rounded-lg shadow-2xl p-5 w-[440px]">
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-3">wizard · invoice-create</div>
    {[
      ["Invoice Number", "DEMO-001", "lookup"],
      ["Supplier", "ACME Corp", "matched"],
      ["Amount", "12,500.00 USD", "validated"],
      ["GL Date", "2026-04-01", "autosuggest"],
    ].map(([k, v, tag], i) => (
      <div key={i} className="flex items-center justify-between border-b border-gray-100 py-2"
        style={{ animation: `fadeUp 0.5s ease ${i * 0.3}s both` }}>
        <div className="text-[11px] text-gray-500 font-mono">{k}</div>
        <div className="text-[12px] font-medium">{v}</div>
        <span className="text-[9px] font-bold tracking-wider uppercase text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
          {tag}
        </span>
      </div>
    ))}
  </div>
);

const MapMockup = () => (
  <div className="bg-[#0B1733] rounded-lg shadow-2xl p-6 w-[440px] relative overflow-hidden">
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-3">telecom · live map</div>
    <svg viewBox="0 0 360 200" className="w-full">
      {/* fake landmass */}
      <path d="M20 120 Q 80 60 160 90 T 340 110 L 340 200 L 20 200 Z" fill="#1B2C58" />
      {/* fibre line (animated) */}
      <path d="M40 150 Q 140 60 320 100" stroke="#C9A84C" strokeWidth="2" fill="none"
        strokeDasharray="240" strokeDashoffset="240" style={{ animation: "drawLine 2.5s ease forwards" }} />
      {/* sites */}
      {[[80, 130], [160, 95], [240, 110], [310, 105]].map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r="6" fill="#0020C2" opacity="0.4"
            style={{ animation: `pulse 1.5s ease-in-out ${i * 0.3}s infinite` }} />
          <circle cx={x} cy={y} r="3" fill="#C9A84C" />
        </g>
      ))}
    </svg>
    <div className="text-[10px] font-mono text-[#C9A84C]/80 mt-2">4 sites · 1 fibre · streaming</div>
  </div>
);

const DbMockup = () => (
  <div className="bg-white rounded-lg shadow-2xl p-4 w-[460px] font-mono text-[11px]">
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-3">vakuai · database</div>
    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
      {[
        "agent_invoice_create",
        "agent_supplier_create",
        "agent_journal_create",
        "agent_receipt_create",
        "telecom_finance_bss",
        "telecom_revenue_assurance",
        "alm_assets",
        "alm_sites",
      ].map((n, i) => (
        <div key={n} className="flex items-center justify-between border-b border-gray-100 pb-1"
          style={{ animation: `fadeUp 0.4s ease ${i * 0.12}s both` }}>
          <span>{n}</span>
          <span className="text-emerald-600 font-bold">●</span>
        </div>
      ))}
    </div>
    <div className="text-[10px] text-gray-500 mt-3">+ 50 more · tenant-tagged</div>
  </div>
);

const AdminMockup = () => (
  <div className="bg-white rounded-lg shadow-2xl p-4 w-[440px]">
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] mb-3">admin · users & access</div>
    {[
      ["sara@bigco", "admin", "5/5 menus"],
      ["jamal@bigco", "maker", "4/5 menus"],
      ["maria@bigco", "checker", "3/5 menus"],
      ["adam@bigco", "user", "2/5 menus"],
    ].map(([email, role, menus], i) => (
      <div key={email} className="flex items-center justify-between py-1.5 border-b border-gray-100"
        style={{ animation: `fadeUp 0.5s ease ${i * 0.25}s both` }}>
        <div className="font-mono text-[11px]">{email}</div>
        <span className="text-[9px] font-bold tracking-wider uppercase bg-blue-50 text-[#0020C2] border border-blue-200 px-1.5 py-0.5">{role}</span>
        <span className="text-[10px] font-mono text-gray-500">{menus}</span>
      </div>
    ))}
  </div>
);

const NarrativeMockup = () => (
  <div className="bg-[#0B1733] rounded-lg shadow-2xl p-5 w-[440px] text-white">
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-2">CFO inbox · 06:42 AM</div>
    <div className="text-[14px] font-bold mb-2">Apr 2026 close — first read</div>
    <div className="text-[11px] leading-relaxed text-white/90 space-y-1.5">
      {[
        "Revenue lands at $48.2M (+6.4% MoM); APAC outperformed plan.",
        "Two anomalies flagged: unusual rebate posting on 04-Apr, and a $310k variance in Site Maintenance.",
        "All 12 ledgers reconciled. Variances explained inline.",
      ].map((t, i) => (
        <p key={i} style={{ animation: `fadeUp 0.6s ease ${i * 0.5}s both` }}>· {t}</p>
      ))}
    </div>
  </div>
);

const MOCKUPS = { chat: ChatMockup, wizard: WizardMockup, map: MapMockup, db: DbMockup, admin: AdminMockup, narrative: NarrativeMockup };

// -----------------------------------------------------------------------------
export default function ProductTourPage() {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showDeck, setShowDeck] = useState(false);
  const tickRef = useRef(null);
  const startRef = useRef(Date.now());

  useEffect(() => { document.title = "Vaku · 90s Product Tour"; }, []);

  useEffect(() => {
    if (!playing) return;
    startRef.current = Date.now();
    tickRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      setProgress(Math.min(elapsed / SLIDE_MS, 1));
      if (elapsed >= SLIDE_MS) {
        setIdx((i) => (i + 1) % SLIDES.length);
        startRef.current = Date.now();
        setProgress(0);
      }
    }, 80);
    return () => clearInterval(tickRef.current);
  }, [playing, idx]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space") { e.preventDefault(); setPlaying((p) => !p); }
      if (e.code === "ArrowRight") setIdx((i) => (i + 1) % SLIDES.length);
      if (e.code === "ArrowLeft") setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const slide = SLIDES[idx];
  const Mockup = MOCKUPS[slide.mockup];
  const Icon = slide.icon;
  const totalMs = useMemo(() => SLIDES.length * SLIDE_MS, []);
  const elapsedMs = idx * SLIDE_MS + progress * SLIDE_MS;

  return (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ backgroundColor: NAVY }}
      data-testid="product-tour">
      {/* styled keyframes ----------------------------------------------------- */}
      <style>{`
        @keyframes fadeUp { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes drawLine { to { stroke-dashoffset: 0; } }
        @keyframes pulse { 0%, 100% { transform: scale(1); opacity: 0.4; } 50% { transform: scale(1.6); opacity: 0; } }
        @keyframes slideIn { 0% { opacity: 0; transform: translateY(20px); } 100% { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Soft gold orb behind content */}
      <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-10 blur-3xl"
        style={{ background: `radial-gradient(circle, ${GOLD}, transparent 60%)` }} />

      {/* Top brand bar -------------------------------------------------------- */}
      <header className="relative max-w-7xl mx-auto px-8 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="tour-brand">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-[22px] font-bold" style={{ background: GOLD, color: NAVY }}>V</div>
          <div className="text-xl font-bold tracking-[1.5px]">VĀ<span style={{ color: GOLD }}>KU</span></div>
        </Link>
        <button onClick={() => setShowDeck(true)} data-testid="tour-deck"
          className="hidden md:inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider bg-[#C9A84C] text-[#0B1733] px-3 py-1.5 hover:bg-[#D8B65A]">
          Get the deck
        </button>
      </header>

      {/* Main slide ---------------------------------------------------------- */}
      <main className="relative max-w-7xl mx-auto px-8 pt-16 pb-32 grid lg:grid-cols-2 gap-12 items-center">
        <div key={`text-${idx}`} style={{ animation: "slideIn 0.6s ease both" }}>
          <div className="flex items-center gap-2 mb-5">
            <Icon size={18} weight="fill" style={{ color: GOLD }} />
            <div className="text-[11px] font-mono uppercase tracking-[0.25em]" style={{ color: GOLD }}>{slide.eyebrow}</div>
          </div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight leading-tight mb-5">{slide.title}</h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-xl">{slide.body}</p>
          {slide.cta && (
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login" data-testid="tour-cta-login"
                className="inline-flex items-center gap-2 bg-[#C9A84C] text-[#0B1733] px-6 py-3 text-sm font-bold uppercase tracking-wider">
                Try it now <ArrowRight size={14} weight="bold" />
              </Link>
              <button onClick={() => setShowDeck(true)} data-testid="tour-cta-deck"
                className="inline-flex items-center gap-2 border border-white/30 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-white/10">
                Get the full deck
              </button>
            </div>
          )}
        </div>

        <div key={`mockup-${idx}`} className="flex justify-center" style={{ animation: "slideIn 0.7s ease 0.1s both" }}>
          {Mockup ? <Mockup /> : null}
        </div>
      </main>

      {/* Bottom controls + progress dots -------------------------------------- */}
      <footer className="fixed bottom-0 left-0 right-0 backdrop-blur-md bg-[#0B1733]/80 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <button onClick={() => setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length)}
            data-testid="tour-prev" className="text-white/70 hover:text-white">
            <CaretLeft size={20} weight="bold" />
          </button>
          <div className="flex-1 flex items-center gap-2">
            {SLIDES.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} data-testid={`tour-dot-${i}`}
                className="flex-1 h-1 bg-white/15 overflow-hidden">
                <div className="h-full" style={{
                  width: i < idx ? "100%" : (i === idx ? `${progress * 100}%` : "0%"),
                  background: GOLD,
                  transition: i === idx ? "none" : "width 0.3s",
                }} />
              </button>
            ))}
          </div>
          <button onClick={() => setPlaying((p) => !p)} data-testid="tour-playpause"
            className="text-white/70 hover:text-white">
            {playing ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
          </button>
          <button onClick={() => setIdx((i) => (i + 1) % SLIDES.length)}
            data-testid="tour-next" className="text-white/70 hover:text-white">
            <CaretRight size={20} weight="bold" />
          </button>
          <div className="text-[10px] font-mono tracking-wider text-white/50 w-16 text-right">
            {Math.round(elapsedMs / 1000)}s / {Math.round(totalMs / 1000)}s
          </div>
        </div>
      </footer>
      {showDeck && <DeckDownloadModal onClose={() => setShowDeck(false)} />}
    </div>
  );
}
